import { POINT_VALUES, pointValue, isOptionSymbol, OPTION_MULTIPLIER } from './tools/contracts';

// Splits CSV text into rows of fields, handling quoted fields with commas,
// escaped quotes ("") and line breaks inside quotes.
function parseRows(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((f) => f.trim() !== ''));
}

// Accepted header names for each trade field, after lowercasing and removing
// spaces, underscores and other punctuation. Earlier names take priority.
const COLUMN_ALIASES = {
  symbol: ['symbol', 'ticker', 'stock', 'contractname', 'contract', 'instrument', 'simbolo'],
  entryPrice: ['entryprice', 'entry', 'buyprice', 'buy', 'openprice', 'preciodeentrada', 'entrada'],
  exitPrice: ['exitprice', 'exit', 'sellprice', 'sell', 'closeprice', 'preciodesalida', 'salida'],
  side: ['side', 'type', 'direction', 'position', 'lado', 'direccion'],
  size: ['size', 'quantity', 'qty', 'contracts', 'shares', 'contratos', 'cantidad'],
  pnl: ['pnl', 'profitloss', 'profit', 'realizedpnl', 'grosspnl', 'resultadobruto'],
  fees: ['fees', 'fee', 'totalfees', 'comisiones'],
  // Kept separate from fees: some exports (e.g. TopstepX) list both, and both are costs.
  commission: ['commission', 'commissions', 'comm'],
  date: ['date', 'tradedate', 'enteredat', 'entrytime', 'opentime', 'exitedat', 'exitdate', 'closedate', 'fecha'],
  exitDate: ['exitedat', 'exittime', 'closetime', 'exitdate', 'closedate'],
  notes: ['notes', 'note', 'comments', 'comment', 'notas'],
  externalId: ['id', 'tradeid', 'orderid'],
  account: ['account', 'accountid', 'accountnumber', 'accountname', 'acct', 'cuenta'],
};

const REQUIRED = ['symbol', 'entryPrice', 'exitPrice'];

// "Símbolo" and "Simbolo" both become "simbolo": accents are dropped before the rest.
function normalizeHeader(h) {
  return h.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function parseNumber(value) {
  let cleaned = value.replace(/[$,\s]/g, '');
  // Accounting style negatives: (123.45)
  if (/^\(.*\)$/.test(cleaned)) cleaned = `-${cleaned.slice(1, -1)}`;
  if (cleaned === '') return NaN;
  return Number(cleaned);
}

function parseSide(value) {
  const v = value.toLowerCase();
  if (['long', 'buy', 'b', 'largo', 'compra'].includes(v)) return 'Long';
  if (['short', 'sell', 's', 'sellshort', 'corto', 'venta'].includes(v)) return 'Short';
  return null;
}

function parseDate(value) {
  // Plain YYYY-MM-DD dates are stored at local noon so they show as the same day.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T12:00:00`);

  // US broker format: MM/DD/YYYY, optionally with a time and a UTC offset,
  // e.g. "10/16/2026 07:46:55 -06:00" or Tradesea's "11/2/2026, 7:31:55 AM CST". Browsers don't
  // parse this consistently.
  const us = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(AM|PM)?)?\s*(Z|[+-]\d{2}:?\d{2}|[ECMP][SD]T)?$/i
  );
  if (us) {
    const [, mo, d, y, h, mi, sec, ampm, offset] = us;
    let hour = h === undefined ? 12 : Number(h);
    if (ampm && ampm.toUpperCase() === 'PM' && hour < 12) hour += 12;
    if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
    const pad = (n) => String(n).padStart(2, '0');
    let iso = `${y}-${pad(mo)}-${pad(d)}T${pad(hour)}:${mi || '00'}:${sec || '00'}`;
    // US time zone names as some brokers write them (Webull: "10:26:18 EDT").
    const ZONES = { EST: '-05:00', EDT: '-04:00', CST: '-06:00', CDT: '-05:00', MST: '-07:00', MDT: '-06:00', PST: '-08:00', PDT: '-07:00' };
    if (offset) {
      const o = offset.toUpperCase();
      iso += o === 'Z' ? 'Z' : ZONES[o] || o.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2');
    }
    const date = new Date(iso);
    return isNaN(date) ? null : date;
  }

  const date = new Date(value);
  return isNaN(date) ? null : date;
}

// Turns CSV text into { trades, errors, missingColumns }.
// Each error is { line, message } using the row number in the file.
export function parseTradesCsv(text) {
  const rows = parseRows(text.replace(/^﻿/, ''));
  if (rows.length === 0) return { trades: [], errors: [], missingColumns: REQUIRED };

  const headers = rows[0].map(normalizeHeader);
  const index = {};
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    const alias = aliases.find((a) => headers.includes(a));
    index[field] = alias ? headers.indexOf(alias) : -1;
  }

  // Robinhood account activity: dates, trans codes and cash rows, read on its own.
  if (headers.includes('activitydate') && headers.includes('transcode') && headers.includes('instrument')) {
    return parseRobinhood(rows.slice(1), headers);
  }

  // Schwab account history: dates, actions and cash rows, read on its own.
  if (headers.includes('action') && headers.includes('feescomm') && headers.includes('amount')) {
    return parseSchwab(rows.slice(1), headers);
  }

  const missingColumns = REQUIRED.filter((f) => index[f] === -1);
  if (missingColumns.length) {
    // Order/fill exports (e.g. Tradovate) list each buy and sell separately.
    const fillIndex = findFillColumns(headers);
    if (fillIndex) {
      // Webull's order history: stocks and options, not futures.
      const webull = headers.includes('placedtime') && headers.includes('filledtime');
      const result = parseFills(rows.slice(1), fillIndex, webull ? 'stocks' : 'futures');
      // Tradesea's Orders export: Time, Symbol ("CME:ES"), Qty, Side, Order Type, Limit/Stop/Avg Price.
      const tradesea = headers.includes('ordertype') && headers.includes('stopprice') && headers.includes('avgprice');
      result.format.platform = webull ? 'Webull' : headers.includes('bs') ? 'Tradovate' : headers.includes('executeprice') && headers.includes('contractname') ? 'TopstepX' : tradesea ? 'Tradesea' : 'Other';
      result.format.hasAccounts = fillIndex.account !== -1;
      return result;
    }
    return { trades: [], errors: [], missingColumns };
  }

  const trades = [];
  const errors = [];
  const seenIds = new Set();
  const fingerprints = new Map();
  rows.slice(1).forEach((row, i) => {
    const line = i + 2;
    const get = (field) => (index[field] === -1 ? '' : (row[index[field]] || '').trim());

    const symbol = get('symbol').toUpperCase();
    const entryPrice = parseNumber(get('entryPrice'));
    const exitPrice = parseNumber(get('exitPrice'));

    if (!symbol) return errors.push({ line, message: 'missing symbol' });
    if (isNaN(entryPrice) || entryPrice < 0) return errors.push({ line, message: 'invalid entry price "{value}"', vars: { value: get('entryPrice') } });
    if (isNaN(exitPrice) || exitPrice < 0) return errors.push({ line, message: 'invalid exit price "{value}"', vars: { value: get('exitPrice') } });

    const trade = { symbol, entryPrice, exitPrice, notes: get('notes') };

    if (get('side')) {
      const side = parseSide(get('side'));
      if (!side) return errors.push({ line, message: 'unknown side "{value}" (use Long or Short)', vars: { value: get('side') } });
      trade.side = side;
    }
    if (get('size')) {
      const size = parseNumber(get('size'));
      if (isNaN(size) || size <= 0) return errors.push({ line, message: 'invalid size "{value}"', vars: { value: get('size') } });
      trade.size = size;
    }
    if (get('pnl')) {
      const pnl = parseNumber(get('pnl'));
      if (isNaN(pnl)) return errors.push({ line, message: 'invalid P&L "{value}"', vars: { value: get('pnl') } });
      trade.pnl = pnl;
    }
    if (get('fees') || get('commission')) {
      const fees = get('fees') ? parseNumber(get('fees')) : 0;
      if (isNaN(fees)) return errors.push({ line, message: 'invalid fees "{value}"', vars: { value: get('fees') } });
      const commission = get('commission') ? parseNumber(get('commission')) : 0;
      if (isNaN(commission)) return errors.push({ line, message: 'invalid commission "{value}"', vars: { value: get('commission') } });
      trade.fees = Math.round((Math.abs(fees) + Math.abs(commission)) * 100) / 100;
    }
    if (get('date')) {
      const date = parseDate(get('date'));
      if (!date) return errors.push({ line, message: 'invalid date "{value}"', vars: { value: get('date') } });
      trade.date = date.toISOString();
    }
    // A separate exit column (when the date came from the entry column) gives the hold time.
    if (index.exitDate !== -1 && index.exitDate !== index.date && get('exitDate')) {
      const exit = parseDate(get('exitDate'));
      if (exit && (!trade.date || exit >= new Date(trade.date))) trade.exitDate = exit.toISOString();
    }
    if (get('account')) trade.accountNumber = get('account');
    if (get('externalId')) {
      if (seenIds.has(get('externalId'))) return errors.push({ line, message: 'trade ID {value} appears twice in this file', vars: { value: get('externalId') } });
      seenIds.add(get('externalId'));
      trade.externalId = get('externalId');
    }
    // No P&L column: work it out from the prices, using the contract's dollar value
    // per point for futures (10 points on MNQ is $20, not $10).
    if (trade.pnl === undefined && pointValue(symbol) !== undefined) {
      const direction = trade.side === 'Short' ? -1 : 1;
      trade.pnl = Math.round((exitPrice - entryPrice) * direction * (trade.size || 1) * pointValue(symbol) * 100) / 100;
    }
    // Files without a trade ID get one made from the trade itself, so importing the
    // same file twice skips the trades already saved.
    if (!trade.externalId && trade.date) {
      const base = `fp-${symbol}-${trade.date}-${trade.side || 'Long'}-${trade.size || 1}-${entryPrice}-${exitPrice}`;
      const n = (fingerprints.get(base) || 0) + 1;
      fingerprints.set(base, n);
      trade.externalId = n > 1 ? `${base}-${n}` : base;
    }
    trades.push(trade);
  });

  const platform = headers.includes('contractname') && headers.includes('enteredat') ? 'TopstepX' : 'Other';
  return {
    trades, errors, missingColumns: [],
    format: { name: 'trades', platform, hasAccounts: index.account !== -1, hasFees: index.fees !== -1, hasCommissions: index.commission !== -1 },
  };
}

// --- Order and fill exports -------------------------------------------------
// Some platforms (e.g. Tradovate's Orders export) list every buy and sell as its
// own row instead of one row per trade. These are rebuilt into round-trip trades:
// a trade opens when the position leaves flat and closes when it returns to flat.

// TopstepX's Orders export uses ContractName, Size, ExecutePrice and FilledAt, and
// Bid/Ask for buy/sell.
const FILL_ALIASES = {
  side: ['bs', 'buysell', 'action', 'side'],
  qty: ['filledqty', 'fillqty', 'filled', 'quantity', 'qty', 'size'],
  price: ['avgfillprice', 'avgprice', 'fillprice', 'executeprice', 'price'],
  time: ['filltime', 'filledtime', 'filledat', 'timestamp', 'time', 'date'],
  contract: ['contract', 'contractname', 'symbol', 'instrument'],
  product: ['product'],
  status: ['status'],
  account: ['account', 'accountname'],
  id: ['orderid', 'fillid', 'id'],
  fees: ['commission', 'commissions', 'comm', 'fees', 'fee', 'totalfees'],
};


// Commission for a whole trade at `perSide` dollars per contract, charged on entry and exit.
export function roundTripCommission(trade, perSide) {
  return Math.round(perSide * 2 * trade.size * 100) / 100;
}

// "MESZ6" -> "MES": strips the futures month code and year.
function productRoot(contract) {
  const m = contract.toUpperCase().match(/^(.+?)[FGHJKMNQUVXZ]\d{1,2}$/);
  return m ? m[1] : contract.toUpperCase();
}

function findFillColumns(headers) {
  const index = {};
  for (const [field, aliases] of Object.entries(FILL_ALIASES)) {
    const alias = aliases.find((a) => headers.includes(a));
    index[field] = alias ? headers.indexOf(alias) : -1;
  }
  const required = ['side', 'qty', 'price', 'time', 'contract'];
  return required.every((f) => index[f] !== -1) ? index : null;
}

// `market` is 'futures' (Tradovate, TopstepX, NinjaTrader) or 'stocks' (Webull): in a stock
// export a plain ticker is a stock, even one that matches a futures code (CL is Colgate).
function parseFills(rows, index, market = 'futures') {
  const errors = [];
  const fills = [];
  let skippedUnfilled = 0;

  rows.forEach((row, i) => {
    const line = i + 2;
    const get = (field) => (index[field] === -1 ? '' : (row[index[field]] || '').trim());

    const status = get('status').toLowerCase();
    if (status && status !== 'filled') {
      skippedUnfilled += 1;
      return;
    }
    const qty = parseNumber(get('qty'));
    const price = parseNumber(get('price'));
    if (!get('qty') && !get('price')) {
      skippedUnfilled += 1;
      return;
    }
    const sideText = get('side').toLowerCase();
    // "Bid" is a buy and "Ask" a sell in TopstepX's export.
    const side = sideText.startsWith('b') ? 1 : sideText.startsWith('s') || sideText === 'ask' ? -1 : 0;
    const time = parseDate(get('time'));
    // Tradesea writes the exchange in front of the symbol ("CME:ES").
    const contract = get('contract').toUpperCase().replace(/^[A-Z_]+:/, '');

    if (!side) return errors.push({ line, message: 'unknown buy/sell value "{value}"', vars: { value: get('side') } });
    if (!contract) return errors.push({ line, message: 'missing contract' });
    if (isNaN(qty) || qty <= 0) return errors.push({ line, message: 'invalid quantity "{value}"', vars: { value: get('qty') } });
    if (isNaN(price)) return errors.push({ line, message: 'invalid price "{value}"', vars: { value: get('price') } });
    if (!time) return errors.push({ line, message: 'invalid time "{value}"', vars: { value: get('time') } });

    let fee = 0;
    if (get('fees')) {
      fee = Math.abs(parseNumber(get('fees')));
      if (isNaN(fee)) return errors.push({ line, message: 'invalid commission "{value}"', vars: { value: get('fees') } });
    }

    fills.push({
      line, side, qty, price, time, contract, fee,
      product: (get('product') || productRoot(contract)).toUpperCase(),
      account: get('account'),
      id: get('id'),
    });
  });

  fills.sort((a, b) => a.time - b.time || a.line - b.line);
  const { trades, stillOpen } = pairFills(fills, errors, market);
  return {
    trades,
    errors,
    missingColumns: [],
    format: {
      name: 'orders',
      filledOrders: fills.length,
      hasFees: index.fees !== -1,
      skippedUnfilled,
      stillOpen,
    },
  };
}

// Walks fills (already in time order) per account + contract, tracking the position, and
// turns each flat-to-flat round trip into a trade. A fill with `kind: 'sell'` (a plain
// sale, as opposed to a short sale) never opens a short: when there's nothing left to sell,
// it was bought before the file starts, so it's counted in `unmatched` and skipped. The
// same goes for `kind: 'cover'` with no short open.
function pairFills(fills, errors, market) {
  const trades = [];
  const open = new Map();
  let unmatched = 0;
  // Fractional shares (e.g. 10.051335) leave float dust like 1e-15; round it away.
  const tidy = (n) => Math.round(n * 1e9) / 1e9;
  for (const fill of fills) {
    const key = `${fill.account}|${fill.contract}`;
    let remaining = fill.qty;
    // An option expiring worthless closes whatever is open, at a price of 0.
    if (fill.kind === 'expire') {
      const openTrade = open.get(key);
      if (!openTrade) {
        unmatched += 1;
        continue;
      }
      fill.side = -openTrade.direction;
      remaining = Math.min(remaining, Math.abs(openTrade.position));
    }
    while (remaining > 0) {
      let trade = open.get(key);
      const cannotOpen = (fill.kind === 'sell' && fill.side === -1) || (fill.kind === 'cover' && fill.side === 1);
      if (cannotOpen && (!trade || trade.direction === fill.side)) {
        unmatched += 1;
        break;
      }
      if (!trade) {
        trade = {
          fill, position: 0, direction: fill.side,
          entryQty: 0, entryValue: 0, exitQty: 0, exitValue: 0, cash: 0, fees: 0,
        };
        open.set(key, trade);
      }
      const opening = fill.side === trade.direction;
      // A fill bigger than the open position closes it and opens a reversed one.
      const qty = opening ? remaining : Math.min(remaining, Math.abs(trade.position));
      trade.position = tidy(trade.position + fill.side * qty);
      trade.cash -= fill.side * qty * fill.price;
      trade.lastTime = fill.time;
      // When one fill closes a trade and opens the next, split its commission by quantity.
      trade.fees += fill.fee * (qty / fill.qty);
      if (opening) {
        trade.entryQty += qty;
        trade.entryValue += qty * fill.price;
      } else {
        trade.exitQty += qty;
        trade.exitValue += qty * fill.price;
      }
      remaining = tidy(remaining - qty);
      if (trade.position === 0) {
        trades.push(finishTrade(trade, errors, market));
        open.delete(key);
      }
    }
  }

  return { trades: trades.filter(Boolean), stillOpen: open.size, unmatched };
}

// Schwab's account history ("Transactions" export): one row per fill or cash movement,
// newest first, with dates but no times. Only buys and sells become trades; dividends,
// fees, interest, transfers and journaled shares are skipped. Rows on the same day keep
// the file's order (reversed, so oldest first) and are marked `timeUnknown`.
const SCHWAB_ACTIONS = {
  'buy': [1, 'buy'], 'sell': [-1, 'sell'], 'sell short': [-1, 'short'], 'buy to cover': [1, 'cover'],
  'buy to open': [1, 'buy'], 'sell to close': [-1, 'sell'], 'sell to open': [-1, 'short'], 'buy to close': [1, 'cover'],
};

function parseSchwab(rows, headers) {
  const col = (name) => headers.indexOf(name);
  const index = { date: col('date'), action: col('action'), symbol: col('symbol'), qty: col('quantity'), price: col('price'), fees: col('feescomm') };
  const errors = [];
  const fills = [];
  let skippedOther = 0;
  const data = rows.map((row, i) => ({ row, line: i + 2 })).reverse();
  data.forEach(({ row, line }, seq) => {
    const get = (k) => (index[k] === -1 ? '' : (row[index[k]] || '').trim());
    const action = SCHWAB_ACTIONS[get('action').toLowerCase()];
    if (!action) {
      skippedOther += 1;
      return;
    }
    const day = get('date').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    const qty = parseNumber(get('qty'));
    const price = parseNumber(get('price'));
    const contract = get('symbol').toUpperCase();
    if (!day) return errors.push({ line, message: 'invalid time "{value}"', vars: { value: get('date') } });
    if (!contract) return errors.push({ line, message: 'missing contract' });
    if (isNaN(qty) || qty <= 0) return errors.push({ line, message: 'invalid quantity "{value}"', vars: { value: get('qty') } });
    if (isNaN(price)) return errors.push({ line, message: 'invalid price "{value}"', vars: { value: get('price') } });
    const fee = get('fees') ? Math.abs(parseNumber(get('fees'))) || 0 : 0;
    // Noon in New York on that day (EDT), one second apart in file order, so trades land on
    // the right trading day and in the right order.
    const time = new Date(Date.UTC(Number(day[3]), Number(day[1]) - 1, Number(day[2]), 16, 0, 0) + seq * 1000);
    fills.push({ line, side: action[0], kind: action[1], qty, price, time, contract, fee, product: contract, account: '', id: '' });
  });
  fills.sort((a, b) => a.time - b.time);
  const { trades, stillOpen, unmatched } = pairFills(fills, errors, 'stocks');
  for (const t of trades) {
    t.timeUnknown = true;
    delete t.exitDate;
  }
  return {
    trades,
    errors,
    missingColumns: [],
    format: { name: 'orders', platform: 'Schwab', filledOrders: fills.length, hasFees: true, skippedUnfilled: 0, skippedOther, unmatched, stillOpen, timeUnknown: true },
  };
}

// Robinhood's account activity export: one row per fill or cash movement, newest first,
// dates only. Trans codes: Buy/Sell (shares), BTO/STC/STO/BTC (options), OEXP (an option
// expiring), SCXL/BCXL (a sell or buy that was cancelled). Everything else (deposits,
// dividends, interest, fees, transferred-in shares) is skipped. Options are written like
// "SPY 4/15/2025 Call $547.00" and turned into OCC symbols so they count as 100 shares.
// Fees aren't listed, so they're the gap between price × quantity and the Amount column.
const ROBINHOOD_CODES = {
  buy: [1, 'buy'], sell: [-1, 'sell'], bto: [1, 'buy'], stc: [-1, 'sell'], sto: [-1, 'short'], btc: [1, 'cover'],
};

function robinhoodOption(description) {
  const m = description.replace(/^Option Expiration for\s+/i, '').match(/^([A-Z.]{1,6})\s+(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(Call|Put)\s+\$([\d,.]+)/i);
  if (!m) return null;
  const pad = (n, len) => String(n).padStart(len, '0');
  const strike = Math.round(Number(m[6].replace(/,/g, '')) * 1000);
  return `${m[1].replace(/\./g, '').toUpperCase()}${m[4].slice(2)}${pad(m[2], 2)}${pad(m[3], 2)}${m[5][0].toUpperCase()}${pad(strike, 8)}`;
}

function parseRobinhood(rows, headers) {
  const col = (name) => headers.indexOf(name);
  const index = { date: col('activitydate'), code: col('transcode'), symbol: col('instrument'), description: col('description'), qty: col('quantity'), price: col('price'), amount: col('amount') };
  const errors = [];
  let fills = [];
  let skippedOther = 0;
  const data = rows.map((row, i) => ({ row, line: i + 2 })).reverse();
  data.forEach(({ row, line }, seq) => {
    const get = (k) => (index[k] === -1 ? '' : (row[index[k]] || '').trim());
    const code = get('code').toLowerCase();
    const action = ROBINHOOD_CODES[code];
    const cancel = code === 'scxl' || code === 'bcxl';
    // The disclaimer at the end of the file has no trans code; it isn't a transaction.
    if (!code) return;
    if (!action && !cancel && code !== 'oexp') {
      skippedOther += 1;
      return;
    }
    const day = get('date').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    const option = robinhoodOption(get('description'));
    const contract = option || get('symbol').toUpperCase();
    // Expirations write the quantity like "2S".
    const qty = code === 'oexp' ? parseFloat(get('qty')) : parseNumber(get('qty'));
    if (!day) return errors.push({ line, message: 'invalid time "{value}"', vars: { value: get('date') } });
    if (!contract) return errors.push({ line, message: 'missing contract' });
    if (isNaN(qty) || qty <= 0) return errors.push({ line, message: 'invalid quantity "{value}"', vars: { value: get('qty') } });
    // Noon in New York that day, one second apart in file order (see parseSchwab).
    const time = new Date(Date.UTC(Number(day[3]), Number(day[1]) - 1, Number(day[2]), 16, 0, 0) + seq * 1000);
    if (code === 'oexp') {
      fills.push({ line, side: 0, kind: 'expire', qty, price: 0, time, contract, fee: 0, product: contract, account: '', id: '' });
      return;
    }
    let price = parseNumber(get('price'));
    if (isNaN(price)) return errors.push({ line, message: 'invalid price "{value}"', vars: { value: get('price') } });
    if (cancel) {
      // Drop the earlier fill this cancels: same contract, side, quantity and price.
      const side = code === 'scxl' ? -1 : 1;
      const match = [...fills].reverse().find((f) => f.contract === contract && f.side === side && Math.abs(f.qty - qty) < 1e-9 && Math.abs(f.price - price) < 0.005);
      if (match) fills = fills.filter((f) => f !== match);
      else skippedOther += 1;
      return;
    }
    const mult = option ? OPTION_MULTIPLIER : 1;
    const amount = Math.abs(parseNumber(get('amount')));
    let fee = 0;
    if (!isNaN(amount)) {
      const value = qty * price * mult;
      const gap = action[0] === 1 ? amount - value : value - amount;
      // A negative gap means the listed price was rounded; use the exact price instead.
      if (gap >= 0) fee = gap;
      else price = amount / (qty * mult);
    }
    fills.push({ line, side: action[0], kind: action[1], qty, price, time, contract, fee, product: contract, account: '', id: '' });
  });
  fills.sort((a, b) => a.time - b.time);
  const { trades, stillOpen, unmatched } = pairFills(fills, errors, 'stocks');
  for (const t of trades) {
    t.timeUnknown = true;
    delete t.exitDate;
  }
  return {
    trades,
    errors,
    missingColumns: [],
    format: { name: 'orders', platform: 'Robinhood', filledOrders: fills.length, hasFees: true, skippedUnfilled: 0, skippedOther, unmatched, stillOpen, timeUnknown: true },
  };
}

// Dollars per point for a fill: options cover 100 shares, futures use their contract
// value, and in a stock export any other symbol is a stock ($1 a point).
function perPointFor(fill, market) {
  if (isOptionSymbol(fill.contract)) return OPTION_MULTIPLIER;
  const futuresMonth = /[FGHJKMNQUVXZ]\d{1,2}$/.test(fill.contract);
  if (market === 'stocks' && !futuresMonth) return 1;
  return POINT_VALUES[fill.product];
}

function finishTrade(t, errors, market) {
  const perPoint = perPointFor(t.fill, market);
  if (perPoint === undefined) {
    errors.push({ line: t.fill.line, message: "unknown contract {value}: can't work out its dollar value per point", vars: { value: t.fill.contract } });
    return null;
  }
  const round = (n) => Math.round(n * 100) / 100;
  const roundPrice = (n) => Math.round(n * 1e6) / 1e6;
  const trade = {
    symbol: t.fill.contract,
    side: t.direction === 1 ? 'Long' : 'Short',
    size: t.entryQty,
    entryPrice: roundPrice(t.entryValue / t.entryQty),
    exitPrice: roundPrice(t.exitValue / t.exitQty),
    // Selling brings cash in and buying spends it, so net cash over the round trip is the P&L.
    pnl: round(t.cash * perPoint),
    fees: round(t.fees),
    date: t.fill.time.toISOString(),
    exitDate: (t.lastTime || t.fill.time).toISOString(),
    notes: '',
  };
  if (t.fill.id) trade.externalId = `${t.fill.account || 'acct'}-${t.fill.id}`;
  // Files without order IDs (Tradesea) get one made from the trade, so importing the same file
  // twice skips the trades already saved.
  else trade.externalId = `fp-${t.fill.account || 'acct'}-${trade.symbol}-${trade.date}-${trade.side}-${trade.size}-${trade.entryPrice}-${trade.exitPrice}`;
  if (t.fill.account) trade.accountNumber = t.fill.account;
  return trade;
}
